import { Component, forwardRef, inject, Injector, input, model, signal } from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { InlineButtonComponent, InlineButtonSize } from '../../atoms/inline-button/inline-button.component';
import { JoiiIcon } from '../../icons/lucide/icon';

export interface SegmentedControllerOption {
  label: string;
  value: string;
  leftIcon?: JoiiIcon;
  rightIcon?: JoiiIcon;
  automationId?: string;
}

@Component({
  selector: 'joii-segmented-controller',
  imports: [InlineButtonComponent],
  templateUrl: './segmented-controller.component.html',
  styleUrl: './segmented-controller.component.css',
  host: {
    '[attr.data-error]': 'hasError ? "true" : null',
    '[attr.data-full-width]': 'fullWidth() ? "true" : "false"',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SegmentedControllerComponent),
      multi: true,
    },
  ],
})
export class SegmentedControllerComponent implements ControlValueAccessor {
  options = input<SegmentedControllerOption[]>([]);
  value = model<string | undefined>();
  size = input<InlineButtonSize>('md');
  fullWidth = input(true);

  private readonly injector = inject(Injector);
  private readonly formDirective = inject(FormGroupDirective, { optional: true });
  protected readonly formDisabled = signal(false);

  /** True when the attached FormControl is invalid and (touched or submitted). */
  get hasError(): boolean {
    const control = this.injector.get(NgControl, null)?.control;
    const submitted = this.formDirective?.submitted ?? false;
    const showError = control?.touched || submitted;
    return !!(control?.invalid && showError);
  }

  private onChange?: (value: string | undefined) => void;
  private onTouched?: () => void;

  writeValue(value: string | undefined): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: string | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled.set(isDisabled);
  }

  selectOption(optionValue: string): void {
    if (this.formDisabled()) {
      return;
    }
    this.value.set(optionValue);
    this.onChange?.(optionValue);
    this.onTouched?.();
  }
}
